import React from "react";
import { H1, H2, H3, H4, StyledLink } from "orcs-design-system";
import { domToReact, attributesToProps } from "html-react-parser";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { AIGraph } from "./AIGraph";

const StyledUl = styled.ul`
  margin: 0;
  padding: ${themeGet("space.s")};
  display: flex;
  flex-direction: column;
  gap: ${themeGet("space.s")};
  li {
    margin: 0;
    margin-left: ${themeGet("space.r")};
    padding: 0;
  }
`;

/**
 * @typedef {Object} HTMLElementProps
 * @property {import("html-react-parser").DOMNode[]} children
 * @property {Object} options
 * @property {Object} [attribs]
 */

export const defaultHTMLElementOverrides = {
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  "final-response": ({ children, options }) => {
    return <div>{domToReact(children, options)}</div>;
  },

  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h1: ({ children, options }) => {
    return (
      <H1
        // @ts-ignore
        pt="8px"
        pb="10px"
      >
        {domToReact(children, options)}
      </H1>
    );
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h2: ({ children, options }) => {
    return (
      <H2
        // @ts-ignore
        pt="8px"
        pb="10px"
      >
        {domToReact(children, options)}
      </H2>
    );
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h3: ({ children, options }) => {
    return (
      <H3
        // @ts-ignore
        pt="8px"
        pb="10px"
      >
        {domToReact(children, options)}
      </H3>
    );
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  h4: ({ children, options }) => {
    return (
      <H4
        // @ts-ignore
        pt="8px"
        pb="10px"
      >
        {domToReact(children, options)}
      </H4>
    );
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  ul: ({ children, options }) => {
    return <StyledUl>{domToReact(children, options)}</StyledUl>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  ol: ({ children, options }) => {
    return <StyledUl>{domToReact(children, options)}</StyledUl>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  a: ({ children, options, attribs }) => {
    const props = attributesToProps(attribs);
    return <StyledLink {...props}>{domToReact(children, options)}</StyledLink>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  p: ({ children, options }) => {
    return <p>{domToReact(children, options)}</p>;
  },
  /**
   * @param {HTMLElementProps} props
   * @returns {React.ReactElement}
   */
  graph: ({ children }) => {
    let specs = [];
    try {
      const originalJSONText =
        children
          // @ts-ignore
          ?.map((c) => c.data)
          ?.filter(Boolean)
          ?.join("") || "{}";

      let jsonText = `[${originalJSONText}]`;

      // Handle where the LLM returns multiple graphs in the same block
      jsonText = originalJSONText
        .replaceAll(/}[\s,]*{/g, "},{")
        .replaceAll("\n", "");

      if (!jsonText.startsWith("[")) {
        jsonText = `[${jsonText}`;
      }
      if (!jsonText.endsWith("]")) {
        jsonText = `${jsonText}]`;
      }

      const jsons = JSON.parse(jsonText);
      specs = jsons.map((spec) => ({ ...spec, title: undefined }));
    } catch (e) {
      return <div />;
    }

    return (
      <>
        {specs.map((spec, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <AIGraph key={i} spec={spec} />
        ))}
      </>
    );
  },
};

/**
 * @param {Object} [htmlElementOverrides={}]
 * @returns {Object}
 */
export const createHtmlParsingOptions = (htmlElementOverrides = {}) => ({
  /**
   * @param {Object} param0
   * @param {Object} param0.attribs
   * @param {React.ReactNode} param0.children
   * @param {string} param0.tagName
   * @returns {React.ReactElement|undefined}
   */
  replace({ attribs, children, tagName }) {
    if (!attribs) {
      return undefined;
    }

    const override =
      htmlElementOverrides[tagName] ||
      defaultHTMLElementOverrides[tagName] ||
      (() => {});

    return override({
      options: createHtmlParsingOptions(htmlElementOverrides),
      children,
      tagName,
      attribs,
    });
  },
});
